import React, { Suspense, useEffect, useMemo, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import ModelScene from "../../components/ModelScene/ModelScene";

const setModelCheckResult = (result) => {
    if (typeof window !== "undefined") {
        window.__MODEL_CHECK_RESULT = result;
    }
};

class ModelCheckErrorBoundary extends React.Component {
    componentDidCatch(error) {
        const message = error?.message || "Viewer rendering failed.";
        setModelCheckResult({
            status: "error",
            error: message,
            loadMs: Date.now() - this.props.startedAt,
        });
    }

    render() {
        return this.props.children;
    }
}

const ViewerStatus = ({ label }) => (
    <div
        style={{
            position: "fixed",
            top: 16,
            left: 16,
            zIndex: 10,
            padding: "8px 12px",
            borderRadius: 999,
            background: "rgba(7, 15, 24, 0.8)",
            color: "#f4f7fb",
            fontFamily: "system-ui, sans-serif",
            fontSize: 12,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
        }}
    >
        {label}
    </div>
);

const PublicModelCheck = () => {
    const startedAtRef = useRef(Date.now());
    const search = typeof window !== "undefined" ? window.location.search : "";
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const glbPath = String(params.get("glb") || "").trim();
    const assemblyPath = String(params.get("json") || "").trim();
    const modelAxis = String(params.get("axis") || "Y_UP").trim();

    useEffect(() => {
        setModelCheckResult({
            status: "loading",
            error: null,
            loadMs: null,
        });

        if (!glbPath) {
            setModelCheckResult({
                status: "error",
                error: "Missing glb query parameter.",
                loadMs: 0,
            });
            return undefined;
        }

        const handleError = (event) => {
            const message =
                event?.error?.message ||
                event?.message ||
                "Unhandled viewer error.";
            setModelCheckResult({
                status: "error",
                error: message,
                loadMs: Date.now() - startedAtRef.current,
            });
        };

        const handleRejection = (event) => {
            const reason = event?.reason;
            const message =
                reason?.message ||
                (typeof reason === "string" ? reason : "Unhandled viewer rejection.");
            setModelCheckResult({
                status: "error",
                error: message,
                loadMs: Date.now() - startedAtRef.current,
            });
        };

        window.addEventListener("error", handleError);
        window.addEventListener("unhandledrejection", handleRejection);

        return () => {
            window.removeEventListener("error", handleError);
            window.removeEventListener("unhandledrejection", handleRejection);
        };
    }, [glbPath]);

    return (
        <div
            style={{
                width: "100vw",
                height: "100vh",
                background:
                    "radial-gradient(circle at top, #18314f 0%, #0b1421 45%, #060b12 100%)",
            }}
        >
            <ViewerStatus label="Model Check" />
            <Canvas
                camera={{ position: [5, 5, 5], fov: 45 }}
                dpr={[1, 1.5]}
                gl={{ antialias: true, preserveDrawingBuffer: true }}
            >
                <ambientLight intensity={1.2} />
                <directionalLight position={[8, 12, 8]} intensity={2.2} />
                <directionalLight position={[-6, 6, -6]} intensity={1.1} />
                <Suspense fallback={null}>
                    <ModelCheckErrorBoundary startedAt={startedAtRef.current}>
                        {glbPath ? (
                            <ModelScene
                                glbPath={glbPath}
                                assemblyPath={assemblyPath || null}
                                modelAxis={modelAxis}
                                onModelLoaded={() => {
                                    requestAnimationFrame(() => {
                                        setModelCheckResult({
                                            status: "loaded",
                                            error: null,
                                            loadMs: Date.now() - startedAtRef.current,
                                        });
                                    });
                                }}
                            />
                        ) : null}
                    </ModelCheckErrorBoundary>
                </Suspense>
            </Canvas>
        </div>
    );
};

export default PublicModelCheck;
